import { useEffect, useState } from "react";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Wallet,
  UserPlus,
  Copy,
  Trash2,
  RotateCcw,
  Crown,
  Loader2,
  Save,
} from "lucide-react";
import { toast } from "sonner";

interface Member {
  id: string;
  email: string;
  name: string;
  role: string;
  is_owner: boolean;
  status: string;
  created_at: string;
}

interface ManualTreasury {
  balance_eur: number;
  label: string;
  note: string;
  updated_at?: string | null;
}

const ROLE_META: Record<string, { label: string; color: string }> = {
  admin: { label: "Admin", color: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  commercial: { label: "Commercial", color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  finance: { label: "Finance", color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  viewer: { label: "Viewer", color: "bg-muted/30 text-muted-foreground border-border" },
};

export default function TeamSettingsPage() {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  // Team state
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [openInvite, setOpenInvite] = useState(false);
  const [invite, setInvite] = useState({ email: "", name: "", role: "commercial" });
  const [busy, setBusy] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<{ email: string; password: string } | null>(null);

  // Treasury state
  const [treasury, setTreasury] = useState<ManualTreasury>({
    balance_eur: 0,
    label: "Compte principal",
    note: "",
    updated_at: null,
  });
  const [loadingTreasury, setLoadingTreasury] = useState(true);
  const [savingTreasury, setSavingTreasury] = useState(false);

  const loadTeam = async () => {
    setLoadingTeam(true);
    try {
      const r = await fetch(`${BACKEND}/api/team/members`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setMembers(d.members || []);
      }
    } finally {
      setLoadingTeam(false);
    }
  };

  const loadTreasury = async () => {
    setLoadingTreasury(true);
    try {
      const r = await fetch(`${BACKEND}/api/treasury/manual`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setTreasury({
          balance_eur: d.balance_eur || 0,
          label: d.label || "Compte principal",
          note: d.note || "",
          updated_at: d.updated_at,
        });
      }
    } finally {
      setLoadingTreasury(false);
    }
  };

  useEffect(() => {
    loadTeam();
    loadTreasury();
  }, []);

  const saveTreasury = async () => {
    setSavingTreasury(true);
    try {
      const r = await fetch(`${BACKEND}/api/treasury/manual`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(treasury),
      });
      if (!r.ok) throw new Error();
      toast.success(`Trésorerie mise à jour : ${treasury.balance_eur.toLocaleString("fr-FR")} €`);
      loadTreasury();
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSavingTreasury(false);
    }
  };

  const sendInvite = async () => {
    if (!invite.email || !invite.name) {
      toast.error("Email et nom requis");
      return;
    }
    setBusy("invite");
    try {
      const r = await fetch(`${BACKEND}/api/team/invite`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(invite),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Échec");
      setInviteResult({ email: d.email, password: d.initial_password });
      setOpenInvite(false);
      setInvite({ email: "", name: "", role: "commercial" });
      loadTeam();
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  const updateRole = async (m: Member, role: string) => {
    try {
      const r = await fetch(`${BACKEND}/api/team/members/${m.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      if (!r.ok) throw new Error();
      toast.success(`Rôle mis à jour : ${role}`);
      loadTeam();
    } catch {
      toast.error("Erreur");
    }
  };

  const removeMember = async (m: Member) => {
    if (!confirm(`Retirer ${m.name} (${m.email}) de l'équipe ?`)) return;
    setBusy(m.id);
    try {
      const r = await fetch(`${BACKEND}/api/team/members/${m.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error();
      toast.success("Membre retiré");
      loadTeam();
    } catch {
      toast.error("Erreur");
    } finally {
      setBusy(null);
    }
  };

  const resetPwd = async (m: Member) => {
    if (!confirm(`Réinitialiser le mot de passe de ${m.email} ?`)) return;
    setBusy(m.id);
    try {
      const r = await fetch(`${BACKEND}/api/team/members/${m.id}/reset-password`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json();
      if (!r.ok) throw new Error();
      setInviteResult({ email: m.email, password: d.new_password });
    } catch {
      toast.error("Erreur");
    } finally {
      setBusy(null);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-6 md:p-10 overflow-y-auto">
          <div className="max-w-5xl mx-auto space-y-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight" data-testid="team-page-title">
                Équipe & Trésorerie
              </h1>
              <p className="text-muted-foreground mt-1">
                Gérez votre équipe et saisissez manuellement le solde de votre banque.
              </p>
            </div>

            {/* Treasury Card */}
            <Card data-testid="manual-treasury-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-primary" />
                  Trésorerie — Saisie manuelle
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Banque non connectée pour des raisons de sécurité. Saisissez votre solde réel ici —
                  Grafton recalculera vos KPIs en temps réel.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-1">
                    <Label htmlFor="balance">Solde actuel (€)</Label>
                    <Input
                      id="balance"
                      type="number"
                      value={treasury.balance_eur}
                      onChange={(e) =>
                        setTreasury({ ...treasury, balance_eur: parseFloat(e.target.value || "0") })
                      }
                      className="text-2xl font-bold"
                      data-testid="treasury-balance-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="label">Libellé du compte</Label>
                    <Input
                      id="label"
                      value={treasury.label}
                      onChange={(e) => setTreasury({ ...treasury, label: e.target.value })}
                      placeholder="Compte principal BNP / Caisse / etc."
                      data-testid="treasury-label-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="note">Note (optionnel)</Label>
                  <Textarea
                    id="note"
                    rows={2}
                    value={treasury.note}
                    onChange={(e) => setTreasury({ ...treasury, note: e.target.value })}
                    placeholder="Mise à jour après réception de la facture #042…"
                    data-testid="treasury-note-input"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs text-muted-foreground">
                    {treasury.updated_at
                      ? `Dernière mise à jour : ${new Date(treasury.updated_at).toLocaleString("fr-FR")}`
                      : "Jamais saisi"}
                  </p>
                  <Button
                    onClick={saveTreasury}
                    disabled={savingTreasury || loadingTreasury}
                    data-testid="treasury-save-btn"
                    className="gap-1.5"
                  >
                    {savingTreasury ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Mettre à jour la trésorerie
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Team Card */}
            <Card data-testid="team-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-primary" />
                    Équipe ({members.length} membre{members.length > 1 ? "s" : ""})
                  </CardTitle>
                  <Button
                    onClick={() => setOpenInvite(true)}
                    data-testid="team-invite-btn"
                    className="gap-1.5"
                  >
                    <UserPlus className="h-4 w-4" /> Inviter un membre
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingTeam ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
                  </div>
                ) : (
                  <div className="space-y-2">
                    {members.map((m) => {
                      const meta = ROLE_META[m.role] || ROLE_META.viewer;
                      return (
                        <div
                          key={m.id}
                          className="border border-border/60 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
                          data-testid={`team-member-${m.id}`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-semibold">
                                {(m.name || m.email).slice(0, 2).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold">{m.name || "—"}</span>
                                {m.is_owner && (
                                  <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/40 gap-1 text-[10px]">
                                    <Crown className="h-3 w-3" /> Owner
                                  </Badge>
                                )}
                                <Badge variant="outline" className={meta.color}>
                                  {meta.label}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{m.email}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {!m.is_owner && (
                              <>
                                <Select
                                  value={m.role}
                                  onValueChange={(v) => updateRole(m, v)}
                                >
                                  <SelectTrigger className="w-[130px] h-8" data-testid={`role-select-${m.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="commercial">Commercial</SelectItem>
                                    <SelectItem value="finance">Finance</SelectItem>
                                    <SelectItem value="viewer">Viewer</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => resetPwd(m)}
                                  disabled={busy === m.id}
                                  data-testid={`reset-pwd-${m.id}`}
                                  title="Réinitialiser mot de passe"
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeMember(m)}
                                  disabled={busy === m.id}
                                  data-testid={`remove-member-${m.id}`}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Invite dialog */}
      <Dialog open={openInvite} onOpenChange={setOpenInvite}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Inviter un membre</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="i-name">Nom complet</Label>
              <Input
                id="i-name"
                value={invite.name}
                onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                placeholder="Marie Dupont"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label htmlFor="i-email">Email</Label>
              <Input
                id="i-email"
                type="email"
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                placeholder="marie@entreprise.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label htmlFor="i-role">Rôle</Label>
              <Select
                value={invite.role}
                onValueChange={(v) => setInvite({ ...invite, role: v })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin (accès total)</SelectItem>
                  <SelectItem value="commercial">Commercial (CRM / Sales / Prospects)</SelectItem>
                  <SelectItem value="finance">Finance (Trésorerie / Factures)</SelectItem>
                  <SelectItem value="viewer">Viewer (lecture seule)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={sendInvite}
              disabled={busy === "invite"}
              className="w-full"
              data-testid="invite-submit"
            >
              {busy === "invite" && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Créer le compte
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Show generated credentials */}
      <Dialog open={!!inviteResult} onOpenChange={() => setInviteResult(null)}>
        <DialogContent className="max-w-md" data-testid="invite-result-dialog">
          <DialogHeader>
            <DialogTitle>Compte créé ✅</DialogTitle>
          </DialogHeader>
          {inviteResult && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Partagez ces identifiants à votre collaborateur (ce mot de passe ne sera plus
                affiché ensuite).
              </p>
              <div className="bg-muted/30 rounded p-3 font-mono text-sm space-y-2">
                <div>
                  <div className="text-xs text-muted-foreground">Email</div>
                  <div>{inviteResult.email}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Mot de passe initial</div>
                  <div className="text-primary">{inviteResult.password}</div>
                </div>
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `Email : ${inviteResult.email}\nMot de passe : ${inviteResult.password}\nLien : ${window.location.origin}/auth`,
                  );
                  toast.success("Identifiants copiés");
                }}
                variant="outline"
                className="w-full gap-1.5"
                data-testid="copy-credentials"
              >
                <Copy className="h-4 w-4" /> Copier (email + mot de passe + lien)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
